import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
  { label: "Services", to: "/#services" },
  { label: "Portfolio", to: "/portfolio" },
  { label: "Méthode", to: "/#methode" },
  { label: "FAQ", to: "/#faq" },
  { label: "Contact", to: "/#contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        data-testid="main-nav"
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "py-3 bg-black/70 backdrop-blur-xl border-b border-white/[0.06]"
            : "py-6 bg-transparent"
        }`}
      >
        <div className="px-5 sm:px-8 lg:px-12 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="font-display text-3xl sm:text-4xl tracking-tighter leading-none"
          >
            ML<span className="text-[var(--accent)]">.</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="font-body text-sm uppercase tracking-[0.18em] text-zinc-400 hover:text-white transition-colors duration-300"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:block">
            <Link
              to="/#contact"
              data-testid="nav-cta"
              className="btn-pill btn-pill-accent text-sm"
            >
              Réserver un audit
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(true)}
            className="md:hidden p-2 -mr-2"
            aria-label="Ouvrir le menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-xl flex flex-col px-6 py-6"
          >
            <div className="flex justify-between items-center">
              <span className="font-display text-3xl">ML<span className="text-[var(--accent)]">.</span></span>
              <button onClick={() => setOpen(false)} aria-label="Fermer le menu" data-testid="nav-mobile-close">
                <X className="w-7 h-7" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center gap-6">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="font-display text-5xl tracking-tighter"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <Link to="/#contact" onClick={() => setOpen(false)} className="btn-pill btn-pill-accent self-start" data-testid="nav-mobile-cta">
              Réserver un audit
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
